// Sales Report Page
import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DollarSign, TrendingUp, FileText, ShoppingCart } from 'lucide-react';
import { useSalesReport } from '@/features/reports/hooks/useSalesReport';
import { PeriodSelector } from '@/features/reports/components/PeriodSelector';
import { MetricCard } from '@/features/reports/components/MetricCard';
import { ExportButton } from '@/features/reports/components/ExportButton';
import { SalesTrendChart, TopProductsChart } from '@/features/reports/components/SalesChart';
import { getThisMonth } from '@/features/reports/utils/periods';
import { formatCurrency } from '@/features/reports/utils/calculations';
import type { ExportFormat } from '@/types/report';

export const Route = createFileRoute('/apps/reports/sales')({  
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: SalesReportPage,
});

function SalesReportPage() {
  const [period, setPeriod] = useState(getThisMonth());
  const { data, isLoading, error } = useSalesReport(period);

  const handleExport = async (format: ExportFormat) => {
    // TODO: Implement export functionality
    console.log('Exporting sales report as', format);
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Erreur lors du chargement du rapport: {error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            <span className="font-arabic">تقرير المبيعات</span>
            <span className="text-muted-foreground mx-2">/</span>
            <span>Rapport des ventes</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Analyse détaillée de vos ventes et performances commerciales
          </p>
        </div>
        
        <ExportButton onExport={handleExport} disabled={isLoading || !data} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Period Selector */}
        <div className="lg:col-span-1">
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>

        {/* Metrics */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard
            title="Chiffre d'affaires TTC"
            titleAr="رقم الأعمال الإجمالي"
            value={data?.metrics.totalRevenue || 0}
            format="currency"
            icon={DollarSign}
            trend={data?.metrics.growthRate}
            color="green"
          />
          
          <MetricCard
            title="Chiffre d'affaires HT"
            titleAr="رقم الأعمال الصافي"
            value={data?.metrics.totalRevenueHT || 0}
            format="currency"
            icon={TrendingUp}
            color="blue"
          />
          
          <MetricCard
            title="TVA collectée"
            titleAr="الضريبة المحصلة"
            value={data?.metrics.totalTVA || 0}
            format="currency"
            icon={FileText}
            subtitle="TVA 19%"
            color="purple"
          />
          
          <MetricCard
            title="Nombre de factures"
            titleAr="عدد الفواتير"
            value={data?.metrics.invoiceCount || 0}
            format="number"
            icon={ShoppingCart}
            subtitle={`Moyenne: ${formatCurrency(data?.metrics.averageInvoice || 0)}`}
            color="orange"
          />
        </div>
      </div>

      {/* Charts */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-80 bg-card border border-border rounded-lg animate-pulse" />
          ))}
        </div>
      ) : data ? (
        <div className="space-y-6">
          <SalesTrendChart data={data.trend} type="line" />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopProductsChart data={data.byProduct} />
            
            {/* Top Clients Table */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                <span className="font-arabic">أفضل العملاء</span>
                <span className="text-muted-foreground mx-2">/</span>
                <span>Top Clients</span>
              </h3>
              
              <div className="space-y-2">
                {data.byClient.slice(0, 10).map((client, index) => (
                  <div
                    key={client.clientId}
                    className="flex items-center justify-between p-3 bg-background rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {client.clientName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {client.invoiceCount} facture{client.invoiceCount > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(client.revenue)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </DashboardLayout>
  );
}
